// helpers
import { addPageAction } from 'shared/helpers/newRelic';
import { getCurrentGrahakAuthSurface } from 'shared/api/grahakAuthState';

export const GRAHAK_RUM_EVENTS = {
  API_REQUEST: 'grahak_api_request',
  FUNCTIONALITY: 'grahak_functionality',
} as const;

type GrahakSurface = 'dweb' | 'mweb' | 'webview';
type GrahakOutcome = 'started' | 'success' | 'failure';
type GrahakFailureCategory =
  | 'client_error'
  | 'rate_limited'
  | 'server_error'
  | 'network_error'
  | 'contract_error';

interface GrahakApiRequestEvent {
  url: string;
  method: string;
  statusCode?: number;
  durationMs: number;
  error?: unknown;
}

interface GrahakFunctionalityEvent {
  functionality: string;
  outcome: GrahakOutcome;
  reason?: string;
  statusCode?: number;
  attempt?: number;
  error?: unknown;
}

const getGrahakSurface = (): GrahakSurface | null => {
  try {
    return getCurrentGrahakAuthSurface();
  } catch (error) {
    return null;
  }
};

const getSurfaceAttributes = () => {
  const surface = getGrahakSurface();

  if (!surface) {
    return {
      grahak_eligible: false,
      grahak_surface: 'disabled',
    };
  }

  return {
    grahak_eligible: true,
    grahak_surface: surface,
    [`grahak_enabled_${surface}`]: true,
  };
};

const getStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return;

  const candidate = error as {
    status?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown };
  };
  const value = candidate.statusCode ?? candidate.status ?? candidate.response?.status;
  const statusCode = Number(value);

  return Number.isFinite(statusCode) && statusCode > 0 ? statusCode : undefined;
};

const getFailureCategory = (
  statusCode?: number,
  error?: unknown
): GrahakFailureCategory | undefined => {
  if (statusCode === 429) return 'rate_limited';
  if (statusCode && statusCode >= 500) return 'server_error';
  if (statusCode && statusCode >= 400) return 'client_error';
  if (error) return error instanceof TypeError ? 'network_error' : 'contract_error';
  return;
};

const getEndpoint = (url: string): string => {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined).pathname;
  } catch (error) {
    return 'unknown';
  }
};

export const isGrahakApiUrl = (url: string): boolean => {
  const endpoint = getEndpoint(url);

  return (
    endpoint.startsWith('/grahak-service/api/') ||
    endpoint.startsWith('/auth/v1/jwt-token/') ||
    endpoint.startsWith('/auth/v1/token/')
  );
};

const trackPageAction = (eventName: string, attributes: Record<string, unknown>): void => {
  if (typeof window === 'undefined' || typeof addPageAction !== 'function') return;

  addPageAction(eventName, {
    ...getSurfaceAttributes(),
    ...attributes,
  });
};

export const trackGrahakApiRequest = ({
  url,
  method,
  statusCode,
  durationMs,
  error,
}: GrahakApiRequestEvent): void => {
  if (!isGrahakApiUrl(url)) return;

  const resolvedStatusCode = statusCode ?? getStatusCode(error);
  const succeeded = Boolean(resolvedStatusCode && resolvedStatusCode < 400);

  trackPageAction(GRAHAK_RUM_EVENTS.API_REQUEST, {
    endpoint: getEndpoint(url),
    method: method.toUpperCase(),
    status_code: resolvedStatusCode ?? 0,
    duration_ms: Math.max(0, Math.round(durationMs)),
    outcome: succeeded ? 'success' : 'failure',
    failure_category: succeeded
      ? 'none'
      : getFailureCategory(resolvedStatusCode, error) || 'network_error',
  });
};

export const trackGrahakFunctionality = ({
  functionality,
  outcome,
  reason = '',
  statusCode,
  attempt,
  error,
}: GrahakFunctionalityEvent): void => {
  const resolvedStatusCode = statusCode ?? getStatusCode(error);

  trackPageAction(GRAHAK_RUM_EVENTS.FUNCTIONALITY, {
    functionality,
    outcome,
    reason,
    status_code: resolvedStatusCode ?? 0,
    failure_category:
      outcome === 'failure'
        ? getFailureCategory(resolvedStatusCode, error) || 'contract_error'
        : 'none',
    ...(attempt !== undefined ? { attempt } : {}),
  });
};
